// "Load More" pager for the collection/search grid.
// Appends the next page via the Section Rendering API instead of numbered pagination,
// and restores the loaded grid + scroll position when returning from a product page.

const RESTORE_FLAG = '__intuitionPlpRestored';

class IntuitionLoadMore extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.total = Number(this.dataset.total) || 0;
    this.button = this.querySelector('.intuition-load-more__button');
    this.countEl = this.querySelector('.intuition-load-more__count');
    this.grid = this.closest('results-list')?.querySelector('[ref="grid"]')
      || document.querySelector('[ref="grid"]');

    if (!this.grid) return;

    if (this.button) {
      this.button.addEventListener('click', () => this.#loadMore());
    }

    this.#restoreIfReturning();
    this.#saveOnLeave();
  }

  async #loadMore() {
    const url = this.button?.dataset.url;
    if (!url || this.button.dataset.loading === 'true') return;

    this.button.dataset.loading = 'true';
    this.button.setAttribute('aria-busy', 'true');
    const label = this.button.textContent;
    this.button.textContent = 'Loading…';

    try {
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}sections=${this.sectionId}`);
      const data = await res.json();
      const doc = new DOMParser().parseFromString(data[this.sectionId], 'text/html');

      const newCards = doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
      this.grid.append(...newCards);

      // Carry forward the next page's URL, or retire the button when exhausted.
      const nextUrl = doc.querySelector('.intuition-load-more__button')?.dataset.url;
      if (nextUrl) {
        this.button.dataset.url = nextUrl;
        this.button.textContent = label;
        this.button.removeAttribute('aria-busy');
        this.button.dataset.loading = 'false';
      } else {
        this.button.remove();
        this.button = null;
      }
      this.#updateCount();
    } catch (e) {
      this.button.textContent = label;
      this.button.removeAttribute('aria-busy');
      this.button.dataset.loading = 'false';
    }
  }

  #updateCount() {
    if (!this.countEl) return;
    const shown = this.grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    this.countEl.textContent = `Showing ${shown} of ${this.total}`;
  }

  #key() {
    return `intuition-plp:${location.pathname}${location.search}`;
  }

  #saveOnLeave() {
    // pagehide covers product-link clicks and is bfcache-friendly.
    window.addEventListener('pagehide', () => {
      try {
        sessionStorage.setItem(this.#key(), JSON.stringify({
          y: window.scrollY,
          html: this.grid.innerHTML,
          nextUrl: this.button ? this.button.dataset.url : '',
        }));
      } catch (e) { /* storage full or unavailable */ }
    });
  }

  #restoreIfReturning() {
    // bfcache restores DOM + scroll itself (connectedCallback won't re-run there).
    // This handles the bfcache-miss case: a back/forward navigation that reloads.
    if (window[RESTORE_FLAG]) return;
    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward') return;

    let state;
    try { state = JSON.parse(sessionStorage.getItem(this.#key())); } catch (e) { return; }
    if (!state || !state.html) return;

    window[RESTORE_FLAG] = true;
    this.grid.innerHTML = state.html;
    if (this.button) {
      if (state.nextUrl) this.button.dataset.url = state.nextUrl;
      else { this.button.remove(); this.button = null; }
    }
    this.#updateCount();
    requestAnimationFrame(() => window.scrollTo(0, state.y));
  }
}

if (!customElements.get('intuition-load-more')) {
  customElements.define('intuition-load-more', IntuitionLoadMore);
}
